import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {boolean} */
  #affirmHandoffInProgress = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener('click', this.#handleAffirmTriggerClick, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener('click', this.#handleAffirmTriggerClick, { capture: true });
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #handleAffirmTriggerClick = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('a.affirm-modal-trigger');
    if (!trigger) return;

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog?.open || this.#affirmHandoffInProgress) return;

    // Close the cart drawer before opening Affirm to avoid stacked modal issues on iOS.
    event.preventDefault();
    event.stopPropagation();

    this.#affirmHandoffInProgress = true;

    try {
      await this.closeDialog();
      this.#cleanupScrollLockState();

      requestAnimationFrame(() => {
        trigger.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      });
    } finally {
      this.#affirmHandoffInProgress = false;
    }
  };

  #cleanupScrollLockState() {
    const hasOpenScrollLockedElement = Boolean(document.querySelector('dialog[scroll-lock][open], details[scroll-lock][open]'));
    if (hasOpenScrollLockedElement) return;

    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
